import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { canSeeAccounting, canSeeHr, canSeeAccountingSidebarItem } from '../../config/modulePermissions';
import { Layout, Menu, Button, Typography, theme, Drawer, Space, Badge, Dropdown, Avatar } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  UserOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  BarChartOutlined,
  SettingOutlined,
  BankOutlined,
  TeamOutlined,
  ToolOutlined,
  FileTextOutlined,
  ContactsOutlined,
  FundOutlined,
  CalculatorOutlined,
  BoxPlotOutlined,
  AuditOutlined,
  ClusterOutlined,
  ThunderboltOutlined,
  StarFilled,
  LogoutOutlined,
  ProfileOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import NotificationCenter from '../realtime/NotificationCenter';
import { useAuthStore } from '../../stores/authStore';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // Check if screen is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 處理登出
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // 用戶下拉菜單
  const userMenuItems = [
    {
      key: 'profile',
      icon: <ProfileOutlined />,
      label: t('個人資料'),
      onClick: () => navigate('/settings/profile')
    },
    // CUSTOMER 角色不顯示設定選項
    ...(user?.role !== 'CUSTOMER' ? [{
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('設定'),
      onClick: () => navigate('/settings')
    }] : []),
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('登出'),
      onClick: handleLogout
    }
  ];

  // Auto-collapse sidebar on mobile
  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
    }
  }, [isMobile]);

  // 根据用户角色过滤菜单项
  const getFilteredMenuItems = () => {
    // CUSTOMER 角色只能看到 Products 和 Stock Management
    if (user?.role === 'CUSTOMER') {
      return [
        {
          key: 'inventory',
          icon: <BoxPlotOutlined />,
          label: t('Inventory'),
          children: [
            {
              key: 'inventory/products',
              label: t('Products'),
            },
            {
              key: 'inventory/stock',
              label: t('Stock Management'),
            },
          ],
        },
      ];
    }

    // Accounting and HR are gated by role — the table is in
    // config/modulePermissions, and the backend enforces the same one on
    // /api/v1/*. Hiding the nav here only avoids 403 dead-ends in the UI.
    //
    // Accounting stays visible for Admin and Sales, who each see a reduced set
    // of tabs once inside; HR is Super Admin only.
    return menuItems
      .filter(item => {
        if (item.key === 'accounting') return canSeeAccounting(user?.role);
        if (item.key === 'hr') return canSeeHr(user?.role);
        return true;
      })
      // The Accounting entries in this sidebar are a second way into the same
      // screens, so they get filtered too — otherwise Admin loses the Ledger tab
      // at the top of the page but keeps "Journal Entries" down the side.
      .map(item =>
        item.key === 'accounting' && (item as any).children
          ? {
              ...item,
              children: (item as any).children.filter((child: any) =>
                canSeeAccountingSidebarItem(user?.role, child.key)),
            }
          : item,
      );
  };

  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: t('Dashboard'),
    },
    {
      key: 'sales',
      icon: <ShoppingCartOutlined />,
      label: t('Sales'),
      children: [
        {
          key: 'sales/orders',
          label: t('Sales Orders'),
        },
        {
          key: 'sales/quotations',
          label: t('Quotations'),
        },
        {
          key: 'sales/invoices',
          label: t('Invoices'),
        },
      ],
    },
    {
      key: 'crm',
      icon: <ContactsOutlined />,
      label: 'CRM',
      children: [
        {
          key: 'crm/pipeline',
          label: t('Sales Pipeline'),
        },
        {
          key: 'crm/leads',
          label: t('Leads'),
        },
        {
          key: 'crm/contacts',
          label: t('Contacts'),
        },
      ],
    },
    {
      key: 'accounting',
      icon: <CalculatorOutlined />,
      label: t('Accounting'),
      children: [
        {
          key: 'accounting',
          label: t('Overview'),
        },
        {
          key: 'accounting/chart-of-accounts',
          label: t('Chart of Accounts'),
        },
        {
          key: 'accounting/journal-entries',
          label: t('Journal Entries'),
        },
        {
          key: 'accounting/invoices',
          label: t('Invoicing'),
        },
        {
          key: 'accounting/vendor-bills',
          label: t('Vendor Bills'),
        },
        {
          key: 'accounting/reports',
          label: t('Financial Reports'),
        },
      ],
    },
    {
      key: 'inventory',
      icon: <BoxPlotOutlined />,
      label: t('Inventory'),
      children: [
        {
          key: 'inventory/dashboard',
          label: t('Dashboard'),
        },
        {
          key: 'inventory/products',
          label: t('Products'),
        },
        {
          key: 'inventory/stock',
          label: t('Stock Management'),
        },
        /*{
          key: 'inventory/warehouses',
          label: 'Warehouses',
        },*/
        {
          key: 'inventory/inbound',
          label: t('Inbound'),
        },
        {
          key: 'inventory/outbound',
          label: t('Outbound'),
        },
        {
          key: 'inventory/picking',
          label: t('Picking Management'),
        },
        {
          key: 'inventory/packing',
          label: t('Packing Management'),
        },
        {
          key: 'inventory/transfer',
          label: t('Internal Transfer'),
        },
        {
          key: 'inventory/returns',
          label: t('Return Management'),
        },
        {
          key: 'inventory/warehouse-management',
          label: t('Warehouse Management'),
        },
      ],
    },
    {
      key: 'purchase',
      icon: <ShopOutlined />,
      label: t('Purchase'),
      children: [
        {
          key: 'purchase/orders',
          label: t('Purchase Orders'),
        },
        {
          key: 'purchase/vendors',
          label: t('Vendors'),
        },
      ],
    },
    {
      key: 'hr',
      icon: <TeamOutlined />,
      label: t('Human Resources'),
      children: [
        {
          key: 'hr/employees',
          label: t('Employees'),
        },
        {
          key: 'hr/payroll',
          label: t('Payroll'),
        },
        {
          key: 'hr/attendance',
          label: t('Attendance'),
        },
      ],
    },
    {
      key: 'manufacturing',
      icon: <ToolOutlined />,
      label: t('Manufacturing'),
      children: [
        {
          key: 'manufacturing/bom',
          label: t('Bill of Materials'),
        },
        {
          key: 'manufacturing/work-orders',
          label: t('Work Orders'),
        },
      ],
    },
    {
      key: 'reports',
      icon: <BarChartOutlined />,
      label: t('Reports'),
      children: [
        {
          key: 'reports/sales',
          label: t('Sales Reports'),
        },
        {
          key: 'reports/financial',
          label: t('Financial Reports'),
        },
        {
          key: 'reports/inventory',
          label: t('Inventory Reports'),
        },
      ],
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('Settings'),
      children: [
        {
          key: 'settings/company',
          label: t('Company Settings'),
        },
        {
          key: 'settings/users',
          label: t('User Management'),
        },
        {
          key: 'settings/permissions',
          label: t('Permissions'),
        },
        {
          key: 'settings/import-export',
          label: t('Import & Export'),
        },
        {
          key: 'settings/audit',
          label: t('Audit Log'),
        },
      ],
    },
    {
      key: 'integrations',
      icon: <ClusterOutlined />,
      label: t('Integrations'),
      children: [
        {
          key: 'integrations/hktvmall',
          label: t('HKTVmall'),
        },
        {
          key: 'integrations/shopify',
          label: t('Shopify'),
        },
      ],
    },
    {
      key: 'test-customization',
      icon: <AuditOutlined />,
      label: '🧪 Test Customization',
      style: { backgroundColor: 'rgba(24, 144, 255, 0.1)' }
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(`/${key}`);
    // Close mobile menu after navigation
    if (isMobile) {
      setMobileMenuVisible(false);
    }
  };

  const getSelectedKeys = () => {
    const path = location.pathname.slice(1); // Remove leading slash
    if (path === '') return ['dashboard'];
    
    // Check if it's a nested route
    for (const item of menuItems) {
      if (item.children) {
        for (const child of item.children) {
          if (path === child.key || path.startsWith(child.key + '/')) {
            return [child.key];
          }
        }
      }
      if (path === item.key || path.startsWith(item.key + '/')) {
        return [item.key];
      }
    }
    
    return [path];
  };

  const getOpenKeys = () => {
    const path = location.pathname.slice(1);
    const openKeys = [];
    
    for (const item of menuItems) {
      if (item.children) {
        for (const child of item.children) {
          if (path === child.key || path.startsWith(child.key + '/')) {
            openKeys.push(item.key);
          }
        }
      }
    }
    
    return openKeys;
  };

  const getPageTitle = () => {
    const path = location.pathname.slice(1);
    
    // Check nested routes first
    for (const item of menuItems) {
      if (item.children) {
        for (const child of item.children) {
          if (path === child.key || path.startsWith(child.key + '/')) {
            return child.label;
          }
        }
      }
      if (path === item.key) {
        return item.label;
      }
    }
    
    return t('Dashboard');
  };

  const renderSidebar = () => (
    <div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={getSelectedKeys()}
        defaultOpenKeys={getOpenKeys()}
        items={getFilteredMenuItems()}
        onClick={handleMenuClick}
        style={{ borderRight: 0 }}
      />
    </div>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sider trigger={null} collapsible collapsed={collapsed} width={250}>
          {renderSidebar()}
        </Sider>
      )}
      
      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          title={t('Menu')}
          placement="left"
          closable={true}
          onClose={() => setMobileMenuVisible(false)}
          open={mobileMenuVisible}
          bodyStyle={{ padding: 0, backgroundColor: '#001529' }}
          width={250}
        >
          {renderSidebar()}
        </Drawer>
      )}

      <Layout>
        <Header style={{ 
          padding: '0 16px', 
          background: colorBgContainer, 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}>
          <Button
            type="text"
            icon={isMobile ? <MenuUnfoldOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
            onClick={() => {
              if (isMobile) {
                setMobileMenuVisible(true);
              } else {
                setCollapsed(!collapsed);
              }
            }}
            style={{
              fontSize: '16px',
              width: 64,
              height: 64,
            }}
          />
          <Space style={{ flex: 1, justifyContent: 'center' }}>
            <Title level={4} style={{ 
              margin: 0, 
              color: '#011f4b',
              fontSize: isMobile ? '16px' : '20px',
            }}>
              {getPageTitle()}
            </Title>
            <Badge 
              count={t('Priority 5')}
              style={{ backgroundColor: '#722ed1' }}
              title={t('Advanced Features Active')}
            />
          </Space>
          <Space>
            <LanguageSwitcher compact={isMobile} />
            <Badge count={t('New')} size="small" color="gold">
              <Button
                type="text"
                icon={<StarFilled style={{ color: '#faad14' }} />}
                size="small"
              />
            </Badge>
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Space style={{ cursor: 'pointer', padding: '8px' }}>
                <Avatar
                  size="small"
                  icon={<UserOutlined />}
                  style={{ backgroundColor: '#005b96' }}
                />
                <span style={{ color: '#011f4b' }}>
                  {user?.name || user?.email || t('用戶')}
                </span>
              </Space>
            </Dropdown>
            <NotificationCenter companyId="demo_company" />
          </Space>
        </Header>
        <Content
          style={{
            margin: isMobile ? '12px 8px' : '24px 16px',
            padding: isMobile ? 12 : 24,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
            minHeight: 280,
            overflow: 'auto',
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout; 